import { Button, Modal, InputNumber, message, Descriptions, Tag, Space } from "antd"
import { useState } from "react"
import axiosInstance from "../../../../../axios/axiosInstance"
import processError from "../../../../../utils/processError"
import type { OrderResponse } from "../../../../../model/response"

interface NapasRefundResponse {
  apiOperation: string
  authorizationResponse?: {
    commercialCardIndicator: string
    date: string
    financialNetworkDate: string
    processingCode: string
    responseCode: string
    stan: string
    time: string
    transactionIdentifier: string
  }
  merchantId: string
  order?: {
    amount: string
    creationTime: string
    currency: string
    id: string
    reference: string
    totalAuthorizedAmount: string
    totalCapturedAmount: string
    totalRefundedAmount: string
  }
  response?: {
    acquirerCode: string
    gatewayCode: string
    message: string
  }
  result: string
  sourceOfFunds?: {
    provided: any
    token: string
    type: string
  }
  timeOfRecord: string
  transaction?: {
    acquirer: any
    amount: number
    currency: string
    id: string
    receipt: string
    type: string
  }
  channel: string
  version: string
}

interface NapasQueryResponse {
  result: string
  order?: {
    amount: string
    creationTime: string
    currency: string
    id: string
    reference: string
    totalAuthorizedAmount: string
    totalCapturedAmount: string
    totalRefundedAmount: string
  }
  response?: {
    acquirerCode: string
    gatewayCode: string
    message: string
  }
  transaction?: {
    acquirer: any
    amount: number
    currency: string
    id: string
    receipt: string
    type: string
  }
  version: string
}

interface Props {
  order: OrderResponse
  onRefreshOrder?: () => void
}

export default function NapasPaymentActions({ order, onRefreshOrder }: Props) {
  const [queryLoading, setQueryLoading] = useState(false)
  const [refundLoading, setRefundLoading] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [showQueryModal, setShowQueryModal] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [refundAmount, setRefundAmount] = useState<number>(0)
  const [queryResult, setQueryResult] = useState<NapasQueryResponse | null>(null)
  const [refundResult, setRefundResult] = useState<NapasRefundResponse | null>(null)
  const [resultType, setResultType] = useState<"query" | "refund">("query")

  const formatPrice = (price?: number) => {
    if (price === undefined) {
      return '';
    }
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price)
  }

  const handleQueryStatus = async () => {
    setQueryLoading(true)
    try {
      const response = await axiosInstance({
        url: `/payment/NAPAS/query-status`,
        method: "get",
        params: { orderId: order.id },
      })

      if (response.status === 200) {
        setQueryResult(response.data)
        setResultType("query")
        setShowQueryModal(true)
        message.success("Truy vấn trạng thái thành công")
      }
    } catch (err) {
      processError(err)
    }
    setQueryLoading(false)
  }

  const handleRefundConfirm = async () => {
    if (!refundAmount || refundAmount <= 0) {
      message.error("Vui lòng nhập số tiền hoàn trả hợp lệ")
      return
    }

    if (refundAmount > order.payment.amount) {
      message.error("Số tiền hoàn trả không được vượt quá số tiền đã thanh toán")
      return
    }

    setRefundLoading(true)
    try {
      const response = await axiosInstance({
        url: `/payment/NAPAS/refund`,
        method: "post",
        params: {
          orderId: order.id,
          amount: refundAmount,
        },
      })

      if (response.status === 200) {
        setRefundResult(response.data)
        setResultType("refund")
        setShowRefundModal(false)
        setShowResultModal(true)
        message.success("Hoàn trả thành công")
        onRefreshOrder?.()
      }
    } catch (err) {
      processError(err)
    }
    setRefundLoading(false)
  }

  const getStatusColor = (result: string) => {
    switch (result?.toUpperCase()) {
      case "SUCCESS":
        return "green"
      case "FAILED":
      case "ERROR":
        return "red"
      case "PENDING":
        return "orange"
      default:
        return "default"
    }
  }

  // Only show for NAPAS payments
  if (order.payment?.paymentChannel !== "NAPAS") {
    return null
  }

  return (
    <>
      <Space>
        <Button size="small" onClick={handleQueryStatus} loading={queryLoading}>
          Truy vấn
        </Button>
        <Button size="small" type="primary" danger onClick={() => setShowRefundModal(true)}>
          Hoàn trả
        </Button>
      </Space>

      {/* Refund Input Modal */}
      <Modal
        title="Hoàn trả thanh toán NAPAS"
        open={showRefundModal}
        onOk={handleRefundConfirm}
        onCancel={() => setShowRefundModal(false)}
        confirmLoading={refundLoading}
        okText="Xác nhận hoàn trả"
        cancelText="Hủy"
      >
        <div className="py-4">
          <div className="mb-4">
            <p>
              <strong>Mã đơn hàng:</strong> {order.orderCode}
            </p>
            <p>
              <strong>Số tiền đã thanh toán:</strong> {formatPrice(order.payment.amount)}
            </p>
          </div>

          <div>
            <label className="block mb-2">Số tiền hoàn trả (VND):</label>
            <InputNumber
              style={{ width: "100%" }}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
              value={refundAmount}
              onChange={(value) => setRefundAmount(value || 0)}
              min={1}
              max={order.payment.amount}
              placeholder="Nhập số tiền muốn hoàn trả..."
            />
            <div className="mt-2 text-sm text-gray-500">Tối đa: {formatPrice(order.payment.amount)}</div>
          </div>
        </div>
      </Modal>

      {/* Query Result Modal */}
      <Modal
        title="Kết quả truy vấn NAPAS"
        open={showQueryModal}
        onCancel={() => setShowQueryModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowQueryModal(false)}>
            Đóng
          </Button>,
        ]}
        width={800}
      >
        {queryResult && (
          <div>
            <Descriptions title="Thông tin giao dịch" bordered column={2}>
              <Descriptions.Item label="Kết quả">
                <Tag color={getStatusColor(queryResult.result)}>{queryResult.result}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Phiên bản">{queryResult.version}</Descriptions.Item>
              <Descriptions.Item label="Mã đơn hàng">{queryResult.order?.id}</Descriptions.Item>
              <Descriptions.Item label="Tham chiếu">{queryResult.order?.reference}</Descriptions.Item>
              <Descriptions.Item label="Số tiền">{formatPrice(Number(queryResult.order?.amount))}</Descriptions.Item>
              <Descriptions.Item label="Tiền tệ">{queryResult.order?.currency}</Descriptions.Item>
              <Descriptions.Item label="Thời gian tạo">{queryResult.order?.creationTime}</Descriptions.Item>
              <Descriptions.Item label="Tổng tiền đã ủy quyền">
                {formatPrice(Number(queryResult.order?.totalAuthorizedAmount))}
              </Descriptions.Item>
              <Descriptions.Item label="Tổng tiền đã thu">
                {formatPrice(Number(queryResult.order?.totalCapturedAmount))}
              </Descriptions.Item>
              <Descriptions.Item label="Tổng tiền đã hoàn">
                {formatPrice(Number(queryResult.order?.totalRefundedAmount))}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="Thông tin phản hồi" bordered column={2} className="mt-4">
              <Descriptions.Item label="Mã acquirer">{queryResult.response?.acquirerCode}</Descriptions.Item>
              <Descriptions.Item label="Mã gateway">{queryResult.response?.gatewayCode}</Descriptions.Item>
              <Descriptions.Item label="Thông báo" span={2}>
                {queryResult.response?.message}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="Thông tin giao dịch" bordered column={2} className="mt-4">
              <Descriptions.Item label="ID giao dịch">{queryResult.transaction?.id}</Descriptions.Item>
              <Descriptions.Item label="Loại giao dịch">{queryResult.transaction?.type}</Descriptions.Item>
              <Descriptions.Item label="Số tiền giao dịch">
                {formatPrice(queryResult.transaction?.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="Tiền tệ">{queryResult.transaction?.currency}</Descriptions.Item>
              <Descriptions.Item label="Biên lai" span={2}>
                {queryResult.transaction?.receipt}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Refund Result Modal */}
      <Modal
        title="Kết quả hoàn trả NAPAS"
        open={showResultModal}
        onCancel={() => setShowResultModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowResultModal(false)}>
            Đóng
          </Button>,
        ]}
        width={800}
      >
        {refundResult && (
          <div>
            <Descriptions title="Kết quả hoàn trả" bordered column={2}>
              <Descriptions.Item label="Kết quả">
                <Tag color={getStatusColor(refundResult.result)}>{refundResult.result}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Thao tác API">{refundResult.apiOperation}</Descriptions.Item>
              <Descriptions.Item label="Merchant ID">{refundResult.merchantId}</Descriptions.Item>
              <Descriptions.Item label="Kênh">{refundResult.channel || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Thời gian ghi nhận">{refundResult.timeOfRecord || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Phiên bản">{refundResult.version || "N/A"}</Descriptions.Item>
            </Descriptions>

            <Descriptions title="Thông tin đơn hàng" bordered column={2} className="mt-4">
              <Descriptions.Item label="ID đơn hàng">{refundResult.order?.id}</Descriptions.Item>
              <Descriptions.Item label="Tham chiếu">{refundResult.order?.reference || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Số tiền đơn hàng">
                {refundResult.order?.amount ? formatPrice(Number(refundResult.order?.amount)) : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Tiền tệ">{refundResult.order?.currency}</Descriptions.Item>
              <Descriptions.Item label="Thời gian tạo">{refundResult.order?.creationTime}</Descriptions.Item>
              <Descriptions.Item label="Tổng tiền đã hoàn">
                <strong className="text-red-600">{formatPrice(Number(refundResult.order?.totalRefundedAmount))}</strong>
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="Thông tin giao dịch hoàn trả" bordered column={2} className="mt-4">
              <Descriptions.Item label="ID giao dịch">{refundResult.transaction?.id}</Descriptions.Item>
              <Descriptions.Item label="Loại giao dịch">{refundResult.transaction?.type}</Descriptions.Item>
              <Descriptions.Item label="Số tiền hoàn trả">
                <strong className="text-red-600">{formatPrice(refundResult.transaction?.amount)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Tiền tệ">{refundResult.transaction?.currency}</Descriptions.Item>
              <Descriptions.Item label="Biên lai" span={2}>
                {refundResult.transaction?.receipt || "N/A"}
              </Descriptions.Item>
            </Descriptions>

            {refundResult.authorizationResponse && (
              <Descriptions title="Phản hồi ủy quyền" bordered column={2} className="mt-4">
                <Descriptions.Item label="Mã phản hồi">
                  <Tag color={refundResult.authorizationResponse.responseCode === "00" ? "green" : "red"}>
                    {refundResult.authorizationResponse.responseCode}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Mã xử lý">
                  {refundResult.authorizationResponse.processingCode}
                </Descriptions.Item>
                <Descriptions.Item label="Ngày">{refundResult.authorizationResponse.date}</Descriptions.Item>
                <Descriptions.Item label="Thời gian">{refundResult.authorizationResponse.time}</Descriptions.Item>
                <Descriptions.Item label="STAN">{refundResult.authorizationResponse.stan}</Descriptions.Item>
                <Descriptions.Item label="Transaction ID">
                  {refundResult.authorizationResponse.transactionIdentifier}
                </Descriptions.Item>
              </Descriptions>
            )}

            <Descriptions title="Phản hồi hệ thống" bordered column={2} className="mt-4">
              <Descriptions.Item label="Mã acquirer">{refundResult.response?.acquirerCode}</Descriptions.Item>
              <Descriptions.Item label="Mã gateway">{refundResult.response?.gatewayCode}</Descriptions.Item>
              <Descriptions.Item label="Thông báo" span={2}>
                {refundResult.response?.message}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </>
  )
}